import { useState } from "react";

import type { UserDto } from "@/api/types";
import { CommentField } from "@/components/CommentField/CommentField";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { useImageInput } from "@/components/ImageInput/useImageInput";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import { isVideoUploaded, useVideoInput } from "@/components/VideoInput/useVideoInput";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { CommentComposerSurface } from "@/product-patterns/tickets/CommentComposer";
import { useTranslation } from "@/translations";

export type ResidentCreateCommentPayload = {
  content: string;
  images?: FormImage[];
  videos: FormVideo[];
  internal?: boolean;
};

interface Props {
  user: UserDto;
  onComment: (payload: ResidentCreateCommentPayload) => Promise<unknown>;
}

export function TicketResidentCommentField({
  user,

  onComment,
}: Props): React.ReactNode {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [images, setImages] = useState<FormImage[]>([]);
  const [videos, setVideos] = useState<FormVideo[]>([]);

  const { addImages, removeImage, removeImages } = useImageInput({ selectedImages: images, onChange: setImages });
  const { uploadFormVideo } = useUploadVideo({
    onProgress: ({ name, progress }) =>
      setVideos((prevState) =>
        prevState.map((video) => {
          if (!isVideoUploaded(video) && video.file.name === name) {
            return { ...video, uploadProgress: progress };
          }

          return video;
        }),
      ),
  });
  const { addVideos, removeVideo, removeVideos } = useVideoInput({
    selectedVideos: videos,
    onChange: setVideos,
    uploadFn: uploadFormVideo,
  });

  const onSubmit = async () => {
    await onComment({
      content: value,
      images,
      videos,
      internal: false,
    });

    removeImages();
    removeVideos();
    setValue("");
  };

  return (
    <CommentComposerSurface>
      <CommentField
        onSubmit={onSubmit}
        user={user}
        allowedAttachments={["image", "video"]}
        tooltips={{
          addImage: t("page.tickets.details.comment-field.resident.image-tooltip"),
          send: t("page.tickets.details.comment-field.resident.send-message-tooltip"),
        }}
        images={images}
        onAddImages={addImages}
        onRemoveImage={removeImage}
        videos={videos}
        onAddVideo={addVideos}
        onRemoveVideo={removeVideo}
        placeholder={t("page.tickets.details.comment-field.resident.placeholder-message")}
        onChange={setValue}
        value={value}
      />
    </CommentComposerSurface>
  );
}
